import React, { useEffect, useState, useRef } from 'react';
import { Space, Select, DatePicker, Row, Col } from 'antd';
import axios from 'axios';
import moment from 'moment';
import echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/graphic';
import 'echarts/lib/component/markLine';
import 'echarts/lib/component/markPoint';
import 'echarts/lib/component/legend';
import 'echarts/lib/chart/bar';
import 'echarts/lib/component/title';

import { dailyEarnChartConfig } from '../../chartConfig';
import './DailyIncome.scss';

const { RangePicker } = DatePicker;

export const DailyIncome = ({ data }) => {
  const dailyChartRef = useRef(null);
  const dailyEarnChartRef = useRef(null);

  const [dailyEarnRangeType, setDailyEarnRangeType] = useState<string>('any'); // any || all

  const [dailyEarnStart, setDailyEarnStart] = useState<string>(moment().format('YYYY') + '-01-01');
  const [dailyEarnEnd, setDailyEarnEnd] = useState<string>(moment().format('YYYY-MM-DD'));

  const getParams = () => {
    if (dailyEarnRangeType !== 'all') {
      return `?startTime=${ dailyEarnStart }&endTime=${ dailyEarnEnd }`;
    }

    return '';
  }

  const getDaily = () => {
    axios.get(`/board/dailyAsset` + getParams()).then((res) => {
      let date = [];
      let data = [];
      const dailyEarnY = [];

      res.data.forEach((item) => {
        date.push(moment(item.date).format('YYYY-MM-DD'));
        data.push(item.total);
        dailyEarnY.push(item.cutOffEarn);
      });

      // 确保 DOM 元素已经渲染
      setTimeout(() => {
        initDaily(data, date);
        setDailyEarnChart(dailyEarnY, date);
      }, 0);
    });
  }

  const initDaily = (data, date) => {
    const dailyElement = document.getElementById('daily');
    if (!dailyElement) return;

    // 如果图表实例已存在，先销毁
    if (dailyChartRef.current) {
      dailyChartRef.current.dispose();
    }

    dailyChartRef.current = echarts.init(dailyElement);

    dailyChartRef.current.setOption({
      tooltip: {
        trigger: 'axis',
        position: function (pt) {
          return [pt[0], '10%'];
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            yAxisIndex: 'none'
          },
          restore: {},
          saveAsImage: {}
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: date
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, '100%']
      },
      dataZoom: [
        {
          type: 'inside',
          start: 0,
          end: 10
        },
        {
          start: 0,
          end: 10
        }
      ],
      series: [
        {
          name: '总净资产',
          type: 'line',
          symbol: 'none',
          sampling: 'lttb',
          itemStyle: {
            color: 'rgb(255, 70, 131)'
          },
          markPoint: {
            data: [
              {
                type: 'max',
                name: '最大值',
                label: {
                  color: '#000'
                },
                itemStyle: {
                  color: 'rgba(255, 158, 68, 0.5)'
                }
              },
              {
                type: 'min',
                name: '最小值',
                label: {
                  color: '#000',
                  // position: 'bottom'
                },
                itemStyle: {
                  color: 'rgba(255, 158, 68, 0.5)'
                }
              }
            ]
          },
          markLine: {
            data: [
              {
                type: 'average',
                name: '平均值',
                label: {
                  position: 'insideStartTop'
                }
              }
            ]
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: 'rgb(255, 158, 68)'
              },
              {
                offset: 1,
                color: 'rgb(255, 70, 131)'
              }
            ])
          },
          data: data
        }
      ]
    });
  }

  const setDailyEarnChart = (data, date) => {
    const dailyEarnElement = document.getElementById('dailyEarn');
    if (!dailyEarnElement) return;

    // 如果图表实例已存在，先销毁
    if (dailyEarnChartRef.current) {
      dailyEarnChartRef.current.dispose();
    }

    dailyEarnChartRef.current = echarts.init(dailyEarnElement);

    dailyEarnChartConfig.xAxis.data = date;
    dailyEarnChartConfig.series[0].data = data;

    dailyEarnChartRef.current.setOption(dailyEarnChartConfig, true);
  }

  const dailyEarnRangeTypeChange = (value: string) => {
    setDailyEarnRangeType(value);
  }

  const dailyEarnRangeChange = (dates, dateStrings) => {
    setDailyEarnStart(dateStrings[0]);
    setDailyEarnEnd(dateStrings[1]);
  }

  useEffect(() => {
    if (dailyEarnStart && dailyEarnEnd || dailyEarnRangeType === 'any') {
      getDaily();
    }
  }, [dailyEarnStart, dailyEarnEnd, dailyEarnRangeType]);

  useEffect(() => {
    // 绘制收益曲线均线
    if (dailyEarnChartRef.current && data.newBonusAll !== undefined) {
      dailyEarnChartConfig.series[0].markLine.data[0].yAxis = data.newBonusAll + '';
      dailyEarnChartRef.current.setOption(dailyEarnChartConfig, true);
    }
  }, [data.newBonusAll]);

  // 处理窗口大小调整
  useEffect(() => {
    const handleResize = () => {
      if (dailyChartRef.current) {
        dailyChartRef.current.resize();
      }
      if (dailyEarnChartRef.current) {
        dailyEarnChartRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      // 组件卸载时清理图表实例
      if (dailyChartRef.current) {
        dailyChartRef.current.dispose();
        dailyChartRef.current = null;
      }
      if (dailyEarnChartRef.current) {
        dailyEarnChartRef.current.dispose();
        dailyEarnChartRef.current = null;
      }
    };
  }, []);

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <h3>每日总净资产曲线</h3>
      <div className="daily-earn-filter">
        <Row gutter={ 16 }>
          <Col xs={ 24 } sm={ 4 } md={ 4 }>
            <Select
              value={ dailyEarnRangeType }
              style={{ width: '100%' }}
              onChange={ (value: string) => { dailyEarnRangeTypeChange(value) }}
              options={[
                {
                  value: 'all',
                  label: '全部',
                }, {
                  value: 'any',
                  label: '自定义',
                }, {
                  value: 'last',
                  label: '去年',
                },
              ]}
            />
          </Col>
          {
            dailyEarnRangeType === 'any' && (
              <Col xs={ 24 } sm={ 4 } md={ 4 } className="range-picker-col">
                <RangePicker
                  ranges={{
                    '最近七天': [moment().add(-8, 'd'), moment().add(-1, 'd')],
                    '最近一个月': [moment().add(-31, 'd'), moment().add(-1, 'd')],
                    '最近三个月': [moment().add(-1 - 30 * 3, 'd'), moment().add(-1, 'd')],
                    '最近半年': [moment().add(-1 - 30 * 6, 'd'), moment().add(-1, 'd')],
                    '最近一年': [moment().add(-1 - 365, 'd'), moment().add(-1, 'd')],
                  }}
                  allowClear={ false }
                  value={ [moment(dailyEarnStart), moment(dailyEarnEnd)] }
                  onChange={ dailyEarnRangeChange }
                  style={{ width: '100%' }}
                />
              </Col>
            )
          }
        </Row>
      </div>
      <div id="daily" style={{ width: '100%', height: 400 }}></div>
      <h3>每日截止收益曲线</h3>
      <div id="dailyEarn" style={{ width: '100%', height: 400 }}></div>
    </Space>
  );
}